import React, { useState, useEffect, useCallback } from 'react';
import { salesApi, warrantyPlanApi, dashboardApi, formatApiErrorDetail, useAuth } from '../../lib/api';
import { formatCurrency, formatDate, downloadCSV } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Pagination } from '../../components/Pagination';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { FileText, Loader2, Download, Eye, ShieldCheck, ShieldX, ShoppingCart, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

export default function SalesHistoryPage() {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 0 });
  const [stats, setStats] = useState(null);

  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      const [salesRes, plansRes, statsRes] = await Promise.all([
        salesApi.list({ page, limit: 20 }),
        warrantyPlanApi.list({ active_only: false }),
        dashboardApi.salesStaff().catch(() => ({ data: null })),
      ]);
      setSales(salesRes.data.items || salesRes.data);
      setPagination({ total: salesRes.data.total || 0, pages: salesRes.data.pages || 0 });
      setPlans(Array.isArray(plansRes.data) ? plansRes.data : (plansRes.data.items || []));
      if (statsRes.data) setStats(statsRes.data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to load sales');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchSales(); }, [fetchSales]);

  const getPlanPrice = (planId) => {
    const plan = plans.find(p => p.id === planId);
    return plan ? plan.warranty_price : 0;
  };
  const getSoldPrice = (sale) => {
    if (!sale.warranty_attached) return 0;
    return sale.warranty_price_override || getPlanPrice(sale.warranty_plan_id);
  };

  const handleExportCSV = () => {
    const exportData = sales.map(sale => ({
      Date: formatDate(sale.sale_date),
      Customer: sale.customer_name,
      Phone: sale.customer_phone,
      Brand: sale.device_brand,
      Model: sale.device_model,
      IMEI: sale.device_imei,
      'Device Price': sale.device_price,
      Warranty: sale.warranty_attached ? 'Yes' : 'No',
      'Plan Price': sale.warranty_attached ? getPlanPrice(sale.warranty_plan_id) : '',
      'Sold Price': sale.warranty_attached ? getSoldPrice(sale) : '',
      Certificate: sale.warranty_certificate || '-',
    }));
    downloadCSV(exportData, `my-sales-${formatDate(new Date())}`);
    toast.success('Sales exported to CSV');
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              My Sales
            </h1>
            <p className="text-slate-600 mt-1">View your sales history</p>
          </div>
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-button">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Monthly Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-slate-200" data-testid="stat-monthly-sales">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                    <ShoppingCart className="h-5 w-5 text-[#005DE8]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.monthly?.sales || 0}</p>
                    <p className="text-xs text-slate-500">Monthly Sales</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200" data-testid="stat-monthly-warranties">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">{stats.monthly?.warranties || 0}</p>
                    <p className="text-xs text-slate-500">Monthly Warranties</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200" data-testid="stat-attach-rate">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-600">{stats.monthly?.attach_rate || 0}%</p>
                    <p className="text-xs text-slate-500">Attach Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200" data-testid="stat-warranty-revenue">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                    <FileText className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{formatCurrency(stats.warranty_revenue || 0)}</p>
                    <p className="text-xs text-slate-500">W. Revenue</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Sales Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" />
              </div>
            ) : sales.length > 0 ? (
              <>
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100">
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead>Warranty</TableHead>
                    <TableHead className="text-right">Plan Price</TableHead>
                    <TableHead className="text-right">Sold Price</TableHead>
                    <TableHead>Approval</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sales.map((sale) => (
                    <TableRow key={sale.id} className="table-row-hover">
                      <TableCell className="text-slate-600">
                        {formatDate(sale.sale_date)}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{sale.customer_name}</p>
                          <p className="text-sm text-slate-500">{sale.customer_phone}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">
                            {sale.device_brand} {sale.device_model}
                          </p>
                          <p className="text-xs text-slate-500">IMEI: {sale.device_imei}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(sale.device_price)}
                      </TableCell>
                      <TableCell>
                        {sale.warranty_attached ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border">Yes</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600 border-slate-200 border">No</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm text-slate-600">
                        {sale.warranty_attached ? formatCurrency(getPlanPrice(sale.warranty_plan_id)) : '-'}
                      </TableCell>
                      <TableCell className="text-right font-medium text-[#005DE8]">
                        {sale.warranty_attached ? formatCurrency(getSoldPrice(sale)) : '-'}
                      </TableCell>
                      <TableCell>
                        {sale.approval_status === 'PENDING' ? (
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200 border">Pending</Badge>
                        ) : sale.approval_status === 'REJECTED' ? (
                          <Badge className="bg-red-50 text-red-700 border-red-200 border">Rejected</Badge>
                        ) : (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border">Approved</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedSale(sale);
                            setShowDetailDialog(true);
                          }}
                          data-testid={`view-sale-${sale.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination page={page} pages={pagination.pages} total={pagination.total} onPageChange={setPage} />
              </>
            ) : (
              <div className="text-center py-16">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No sales yet</h3>
                <p className="text-slate-500 mt-1">Your sales will appear here once you record them</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sale Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Sale Details</DialogTitle>
            </DialogHeader>
            {selectedSale && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Date</p>
                    <p className="font-medium">{formatDate(selectedSale.sale_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Sale ID</p>
                    <p className="font-medium text-xs">{selectedSale.id}</p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Customer</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-slate-500">Name:</span>
                    <span>{selectedSale.customer_name}</span>
                    <span className="text-slate-500">Phone:</span>
                    <span>{selectedSale.customer_phone}</span>
                    {selectedSale.customer_email && (
                      <>
                        <span className="text-slate-500">Email:</span>
                        <span>{selectedSale.customer_email}</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Device</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-slate-500">Brand:</span>
                    <span>{selectedSale.device_brand}</span>
                    <span className="text-slate-500">Model:</span>
                    <span>{selectedSale.device_model}</span>
                    <span className="text-slate-500">IMEI:</span>
                    <span className="font-mono">{selectedSale.device_imei}</span>
                    <span className="text-slate-500">Price:</span>
                    <span className="font-semibold">{formatCurrency(selectedSale.device_price)}</span>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Warranty</h4>
                  {selectedSale.warranty_attached ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <ShieldCheck className="h-5 w-5 text-emerald-600" />
                        <span className="font-medium text-emerald-800">Warranty Attached</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                        <div><span className="text-slate-500">Plan Price:</span> <span className="font-medium">{formatCurrency(getPlanPrice(selectedSale.warranty_plan_id))}</span></div>
                        <div><span className="text-slate-500">Sold Price:</span> <span className="font-semibold text-[#005DE8]">{formatCurrency(getSoldPrice(selectedSale))}</span></div>
                      </div>
                      {selectedSale.warranty_certificate && (
                        <p className="text-sm mt-2">
                          Certificate: <span className="font-mono font-medium">{selectedSale.warranty_certificate}</span>
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <ShieldX className="h-5 w-5 text-slate-400" />
                        <span className="text-slate-600">No warranty attached</span>
                      </div>
                      {selectedSale.warranty_override_reason && (
                        <p className="text-sm text-slate-500 mt-2">
                          Reason: {selectedSale.warranty_override_reason}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
